import { useCallback, useEffect, useRef } from "react";

import type { TextInputProps } from "@ui/components/Inputs";
import { TextInput } from "@ui/components/Inputs";
import { useTranslations } from "@ui/providers/useTranslations";

import { useConfig } from "@jti/app-context/useConfig";
import { getAddressComponentByType, useGoogleAPI } from "@jti/fe-clients";

import type { PlaceInputResults } from "../..";
import { initAutocomplete } from "../../initAutocomplete";

type AddressComponentsTypes = Record<string, string[] | undefined> | undefined;

export type GooglePlaceInputProps = {
  onPlace?: (result: PlaceInputResults) => void;
} & TextInputProps;

const prepareGoogleAutocompleteResults = (
  place: google.maps.places.PlaceResult,
  addressComponentTypes: AddressComponentsTypes = {}
): PlaceInputResults => {
  const cityComponentTypes = addressComponentTypes.city ?? ["locality", "postal_town", "sublocality_level_1"];

  return {
    city: getAddressComponentByType(place, cityComponentTypes)?.long_name ?? "",
    /*     
    Barangay is the smallest administrative division in the Philippines and is equivalent to a district in other countries
    We use the variable named *districtName* to store this value
    */
    districtName:
      getAddressComponentByType(place, ["sublocality", "sublocality_level_1", "sublocality_level_1"])?.long_name ?? "",
    postcode: getAddressComponentByType(place, ["postal_code"])?.long_name ?? "",
    provinceName: getAddressComponentByType(place, ["administrative_area_level_1"])?.long_name ?? "",
    regionCode: getAddressComponentByType(place, ["administrative_area_level_1"])?.short_name ?? "",
    streetName: getAddressComponentByType(place, ["route"])?.long_name ?? "",
    streetNumber: getAddressComponentByType(place, ["street_number"])?.long_name ?? "",
  };
};

export const GooglePlaceInput = ({ onPlace, ref: forwardedRef, ...props }: GooglePlaceInputProps) => {
  const { __ } = useTranslations();
  const _google = useGoogleAPI();
  const config = useConfig();
  const ref = useRef<HTMLInputElement>(null);

  const onResults = useCallback(
    (place: google.maps.places.PlaceResult) => {
      const addressComponentTypes = {
        city: config.googleMapsCityComponentType ? config.googleMapsCityComponentType.split(",") : undefined,
      };

      if (onPlace) {
        onPlace({
          ...prepareGoogleAutocompleteResults(place, addressComponentTypes),
          ...(config.isSingleFieldAddressLookup && { singleFieldAddress: ref.current?.value ?? "" }),
        });
      }
    },
    [onPlace]
  );

  useEffect(() => {
    if (typeof ref !== "function" && ref?.current && _google) {
      initAutocomplete(_google, ref.current, config.ISO_COUNTRY_CODE, onResults);
    }
    // adding onPlace to useEffect dependencies will end with multiple unwanted initializations
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [_google, ref]);

  // Combine the internal ref (needed for initAutocomplete) with the forwarded ref
  // so react-hook-form can focus this field when it is the first invalid field.
  const combinedRef = useCallback(
    (node: HTMLInputElement | null) => {
      ref.current = node;
      if (typeof forwardedRef === "function") {
        forwardedRef(node);
      } else if (forwardedRef) {
        forwardedRef.current = node;
      }
    },
    [forwardedRef]
  );

  return (
    <TextInput
      aria-label={__("address_fields.street_name")}
      autoComplete="off"
      {...props}
      data-testid="placeAutocompleteInput"
      ref={combinedRef}
    />
  );
};
